// Types for the `quasar-shopdata-changelog` Elasticsearch index, which records
// every push made through the Curators admin-shop editor.

/**
 * A Minecraft text component as it appears inside the changelog payload.
 * The wire format is permissive — page names can come through as either:
 *   - a raw string (often containing legacy `§` color codes), or
 *   - a parsed `{ text, color, extra, ... }` JSON component (single or array).
 *
 * The `MinecraftPageName` helper dispatches on `typeof value === 'string'`.
 */
export type ShopChangelogMcText = string | object | object[];

export type ShopChangelogUserIdType = 'discord' | 'uuid';

export interface ShopChangelogTotals {
  pagesAdded: number;
  pagesRemoved: number;
  pagesRenamed: number;
  entriesAdded: number;
  entriesRemoved: number;
  entriesModified: number;
}

export interface ShopChangelogEntryStack {
  /** Namespaced item id, e.g. `minecraft:diamond_pickaxe`. */
  id: string;
  count: number;
  components?: Record<string, unknown>;
}

export interface ShopChangelogEntry {
  type: 'LISTING' | 'PAGE';
  /** Chest-grid slot, 0..53. */
  slot: number;
  stack: ShopChangelogEntryStack;
  /** -1 means "not buyable". Only meaningful for `type === 'LISTING'`. */
  buyPrice?: number;
  /** -1 means "not sellable". Only meaningful for `type === 'LISTING'`. */
  sellPrice?: number;
  /** Batch size per transaction. */
  count?: number;
  /** Target page id — only meaningful for `type === 'PAGE'`. */
  pageId?: string;
  // Allow additional fields without complaint — the diff format may reference them.
  [key: string]: unknown;
}

export interface ShopChangelogFieldChange {
  /** Dotted path like `buyPrice`, `sellPrice`, `count`, `stack.id`, `stack.count`, `pageId`. */
  field: string;
  before: unknown;
  after: unknown;
}

export interface ShopChangelogModifiedEntry {
  slot: number;
  before: ShopChangelogEntry;
  after: ShopChangelogEntry;
  fields: ShopChangelogFieldChange[];
}

export interface ShopChangelogPagesEntriesChanged {
  pageId: string;
  pageNameBefore: ShopChangelogMcText;
  pageNameAfter: ShopChangelogMcText;
  added: ShopChangelogEntry[];
  removed: ShopChangelogEntry[];
  modified: ShopChangelogModifiedEntry[];
}

export interface ShopChangelogPageRename {
  id: string;
  before: {
    name?: ShopChangelogMcText;
    description?: ShopChangelogMcText;
    [k: string]: unknown;
  };
  after: {
    name?: ShopChangelogMcText;
    description?: ShopChangelogMcText;
    [k: string]: unknown;
  };
  fields: ShopChangelogFieldChange[];
}

export interface ShopChangelogPage {
  id: string;
  name?: ShopChangelogMcText;
  description?: ShopChangelogMcText;
  entries?: ShopChangelogEntry[];
  [key: string]: unknown;
}

export interface ShopChangelogChange {
  pagesAdded?: ShopChangelogPage[];
  pagesRemoved?: ShopChangelogPage[];
  pagesRenamed?: ShopChangelogPageRename[];
  pagesEntriesChanged?: ShopChangelogPagesEntriesChanged[];
}

export interface ShopChangelogDoc {
  '@timestamp': string;
  networkId: string;
  /** Real cluster id (e.g. `SUNCOB`, `ATM10`) or the special `_GLOBAL_` bucket. */
  clusterId: string;
  userId: string | null;
  userIdType: ShopChangelogUserIdType;
  sessionJti: string;
  ip: string;
  userAgent: string;
  totals: ShopChangelogTotals;
  affectedPageIds: string[];
  changelog: ShopChangelogChange;
}

export interface ShopChangelogHit {
  _index: string;
  _id: string;
  _score: number | null;
  _source: ShopChangelogDoc;
  sort?: unknown[];
}

export interface ShopChangelogResponse {
  took: number;
  timed_out: boolean;
  hits: {
    total: { value: number; relation: string };
    max_score: number | null;
    hits: ShopChangelogHit[];
  };
}

/** UI filter state owned by `ShopChangelogView`. */
export interface ShopChangelogFilters {
  networkIds: string[];
  clusterIds: string[];
  userIds: string[];
  affectedPageIds: string[];
  dateRange: { from: Date; to: Date };
  destructiveOnly: boolean;
}

export const GLOBAL_CLUSTER_ID = '_GLOBAL_';
